/*
  Carter & Me — wishlist heart.

  Theme-native persistence: the saved handles live in localStorage under
  cm:wishlist. That covers one browser, which is enough for the heart to
  feel real and for the wishlist page to have something to render.

  Phase 8 adds the customer half: when someone is logged in, the list
  syncs to a customer metafield so it follows them between devices.
  Until then this is deliberately per-browser, not per-customer.

  Every toggle dispatches cm:wishlist:change so anything else on the page
  (a count in the header, the wishlist grid) can react without polling.
*/

const CM_WISHLIST_KEY = 'cm:wishlist';

function cmWishlistRead() {
  try {
    const raw = localStorage.getItem(CM_WISHLIST_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    // Private mode, blocked storage, or corrupt JSON. Behave as empty.
    return [];
  }
}

function cmWishlistWrite(handles) {
  try {
    localStorage.setItem(CM_WISHLIST_KEY, JSON.stringify(handles));
  } catch (e) {
    // Nothing to do: the heart still reflects this page view.
  }
  document.dispatchEvent(new CustomEvent('cm:wishlist:change', { detail: { handles } }));
}

function cmWishlistHas(handle) {
  return cmWishlistRead().includes(handle);
}

function cmWishlistToggle(handle) {
  const handles = cmWishlistRead();
  const index = handles.indexOf(handle);
  if (index === -1) {
    handles.push(handle);
  } else {
    handles.splice(index, 1);
  }
  cmWishlistWrite(handles);
  return index === -1;
}

// The header count. Liquid cannot know it, so it is rendered hidden and
// revealed here. Hidden entirely at zero rather than showing "(0)": a
// saved list nobody can see they have is a list nobody comes back to,
// but a permanent empty counter is just noise pointing at an empty page.
function cmWishlistCount(saved) {
  const handles = saved || cmWishlistRead();
  // Anything that only makes sense once something is saved: the header
  // count, and the band offering to carry the list into a registry.
  document.querySelectorAll('[data-cm-wishlist-when-any]').forEach((el) => {
    el.hidden = handles.length === 0;
  });
  document.querySelectorAll('[data-cm-wishlist-count]').forEach((el) => {
    el.textContent = String(handles.length);
  });
}

// Reflect stored state onto every heart currently in the DOM.
function cmWishlistSync(root = document) {
  const saved = cmWishlistRead();
  cmWishlistCount(saved);
  root.querySelectorAll('[data-cm-wishlist]').forEach((button) => {
    const handle = button.dataset.productHandle;
    const on = saved.includes(handle);
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      `${on ? 'Remove' : 'Save'} ${button.dataset.productTitle || 'this piece'} ${
        on ? 'from' : 'to'
      } wishlist`
    );
  });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cm-wishlist]');
  if (!button) return;
  event.preventDefault();
  cmWishlistToggle(button.dataset.productHandle);
  cmWishlistSync();
});

// Grids arrive after filtering or paging, so re-sync when the list changes.
document.addEventListener('cm:wishlist:change', () => cmWishlistSync());
document.addEventListener('DOMContentLoaded', () => cmWishlistSync());
if (document.readyState !== 'loading') cmWishlistSync();
